/**
 * DayCountdownChip — P1.1 · client-side countdown on locked days
 *
 * When a day has `day_unlock_at` set and that moment is in the future,
 * render a live ticker: "Auto-reveals in 3d 4h 17m". Updates every minute.
 */
import React from "react";
import { Lock } from "lucide-react";

function diffParts(targetMs) {
  let s = Math.max(0, Math.round((targetMs - Date.now()) / 1000));
  const days = Math.floor(s / 86400); s -= days * 86400;
  const hours = Math.floor(s / 3600); s -= hours * 3600;
  const minutes = Math.floor(s / 60);
  return { days, hours, minutes, total: targetMs - Date.now() };
}

function fmt({ days, hours, minutes }) {
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${Math.max(0, minutes)}m`;
}

export default function DayCountdownChip({ unlockAt, dayIdx }) {
  const targetMs = React.useMemo(() => {
    try { return new Date(unlockAt).getTime(); } catch { return 0; }
  }, [unlockAt]);

  const [parts, setParts] = React.useState(() => diffParts(targetMs));

  React.useEffect(() => {
    if (!targetMs) return undefined;
    const id = setInterval(() => setParts(diffParts(targetMs)), 60_000);
    return () => clearInterval(id);
  }, [targetMs]);

  if (!targetMs || parts.total <= 0) return null;

  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[hsl(var(--accent-gold))]/12 ring-1 ring-[hsl(var(--accent-gold))]/30 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))]"
      data-testid={`client-itinerary-day-${dayIdx}-countdown`}
      title={`Auto-reveals on ${new Date(targetMs).toLocaleString()}`}
    >
      <Lock className="size-3" strokeWidth={1.5} />
      Auto-reveals in {fmt(parts)}
    </span>
  );
}
